import operationTypesRealNames from 'constants/operationTypesRealNames';
import {
  getBeneficiaryAmount,
  getBeneficiaryFDPercent,
  getVestedUnits,
} from 'utils/filters';
import { Beneficiary, Society, SocietyValue, StockPlan } from 'types';
import invitationStatus from 'constants/invitationStatus';
import isSuperAdmin from 'utils/isSuperAdmin';
import { getStatus, getStatusName, getUnits } from './beneficiaryStatus';
import {
  getConsolidatedPercent,
  getConsolidationDate,
  getMonthsToVest,
  getStrikePrice,
  getToVest,
} from './beneficiaryPlanCalculations';

export const getPlanName = (planId: string, plans: StockPlan[]): string => {
  const plan = plans?.find((plan) => plan['_id'] === planId);
  return plan ? plan.name : '-';
};

export const getIncentive = (planId: string, plans: StockPlan[]): string => {
  const plan = plans?.find((plan) => plan['_id'] === planId);
  return plan ? operationTypesRealNames[plan.planType] : '-';
};

export const getCertificateData = (
  beneficiary: Beneficiary,
  planId: string,
  plans: StockPlan[],
  actualSociety: Society,
  societyId: string
) => {
  const plan = plans?.find((plan) => plan['_id'] === planId);

  const beneficiaryData = {
    ...beneficiary,
    sharesCount: beneficiary.sharesCount,
    amount: getBeneficiaryAmount(beneficiary, plan),
    cif: beneficiary.cif,
  };
  const planData = {
    sharePrice: plan?.sharePrice,
    startDate: plan?.startDate,
    planId,
  };
  const societyData = {
    name: actualSociety?.name,
    cif: actualSociety?.cif,
    administrator:
      actualSociety?.mainAdmin || actualSociety?.administrators?.[0],
    societyId,
  };

  return { societyData, beneficiaryData, planData };
};

export const getIncentiveValue = (
  FD: number,
  societyValue: SocietyValue
): number => (societyValue ? (+(FD || 0) * societyValue.value) / 100 : 0);

export const parseBeneficiaryRowToTable = ({
  props,
  plan,
  plans,
  actualSociety,
  filterOptions,
  currentBeneficiaryOptions,
  isAdmin,
  isReadOnly,
  user,
  t,
}: any) => ({
  beneficiary: props.beneficiary,
  name: props.beneficiary?.name,
  email: props.beneficiary?.email,
  cif: props.beneficiary?.cif || '-',
  status: getStatus(props.beneficiary, props.beneficiary?.plan, plans),
  statusName: getStatusName(props.beneficiary, props.beneficiary?.plan, plans),
  planId: props.beneficiary?.plan,
  societyId: actualSociety._id,
  units: getUnits(props.beneficiary),
  decimals: props.decimals,
  amount: props.amount,
  FDpercent: getBeneficiaryFDPercent(props.beneficiary, props.society),
  jobTitle: props.beneficiary?.jobTitle || '-',
  incentive: getIncentive(props.beneficiary?.plan, plans),
  planName: getPlanName(props.beneficiary?.plan, plans),
  consolidatedPercent: getConsolidatedPercent(
    props.beneficiary,
    props.beneficiary?.plan,
    plans
  ),
  consolidationDate: getConsolidationDate(
    props.beneficiary,
    props.beneficiary?.plan,
    plans
  ),
  planStartDate: new Date(props.beneficiary?.planStartDate),
  vestedUnits: getVestedUnits(props.beneficiary, plan),
  toVestUnits: getToVest(props.beneficiary, plan),
  months: getMonthsToVest(props.beneficiary, props.beneficiary?.plan, plans),
  salary: props.beneficiary?.salary || '-',
  laboralCategory: props.beneficiary?.laboralCategory?.name || '-',
  strikePrice: getStrikePrice(
    props.beneficiary,
    props.beneficiary?.plan,
    plans
  ),
  certificateData: getCertificateData(
    props.beneficiary,
    props.beneficiary?.plan,
    plans,
    actualSociety,
    actualSociety._id
  ),
  documentURL: props.beneficiary?.documentURL,
  beneficiaryOptions: filterOptions(
    props.beneficiary,
    currentBeneficiaryOptions,
    actualSociety._id,
    isAdmin,
    user,
    t
  ),
  invitationStatus:
    invitationStatus[
      props.beneficiary?.invitationStatus as keyof typeof invitationStatus
    ],
  user,
  isSuperAdmin: isSuperAdmin(user),
  isReadOnly,
});
